import { hexToOklch, oklchToHexInGamut } from "./oklch";

export interface AccentSet {
  fill: string;
  hover: string;
  pressed: string;
  on: string;
  ink: string;
  soft: string;
}

export interface ClientAccent {
  light: AccentSet;
  dark: AccentSet;
}

type ThemeName = "light" | "dark";

interface ThemeSurfaces {
  page: string;
  raised: string;
  sunken: string;
  muted: string;
  inkDefault: string;
  softAlpha: number;
}

const THEMES: Record<ThemeName, ThemeSurfaces> = {
  light: { page: "#F7F4EE", raised: "#FFFDF9", sunken: "#EFEBE3", muted: "#655E52", inkDefault: "#1C1A16", softAlpha: 0.12 },
  dark: { page: "#161512", raised: "#211F1B", sunken: "#100F0D", muted: "#9F978A", inkDefault: "#EEEAE2", softAlpha: 0.18 },
};

const ON_CANDIDATES = ["#FFFDF9", "#1C1A16"] as const;
const BRAND_PATTERN = /^#[0-9A-F]{6}$/i;
const MIN_TEXT_CONTRAST = 4.5;
const MIN_FILL_CONTRAST_DARK = 3;
const LIGHTNESS_STEP = 0.01;
const INK_LIGHTNESS_STEP = 0.005;
const SOFT_ALPHA_STEP = 0.01;
const SOFT_ALPHA_FLOOR = 0.06;
const HOVER_OVERLAY = 0.08;
const PRESSED_OVERLAY = 0.14;
const MAX_ADJUSTMENTS = 200;

function channelLuminance(hex: string, start: number): number {
  const c = parseInt(hex.slice(start, start + 2), 16) / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function luminance(hex: string): number {
  return 0.2126 * channelLuminance(hex, 1) + 0.7152 * channelLuminance(hex, 3) + 0.0722 * channelLuminance(hex, 5);
}

function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/** Mezcla alfa en sRGB de `foreground` al `alpha` sobre `background`. */
function blend(foreground: string, background: string, alpha: number): string {
  const channels = [1, 3, 5].map((start) => {
    const fg = parseInt(foreground.slice(start, start + 2), 16);
    const bg = parseInt(background.slice(start, start + 2), 16);
    return Math.round(fg * alpha + bg * (1 - alpha))
      .toString(16)
      .padStart(2, "0");
  });
  return `#${channels.join("").toUpperCase()}`;
}

function shiftLightness(hex: string, delta: number): string {
  const { l, c, h } = hexToOklch(hex);
  return oklchToHexInGamut(Math.max(0, Math.min(1, l + delta)), c, h);
}

const bestOn = (fill: string) => ON_CANDIDATES.reduce((best, o) => (contrast(o, fill) > contrast(best, fill) ? o : best));

function liftFillAboveDarkPage(brand: string, page: string): string {
  let fill = brand;
  for (let i = 0; i < MAX_ADJUSTMENTS && contrast(fill, page) < MIN_FILL_CONTRAST_DARK; i++) {
    fill = shiftLightness(fill, LIGHTNESS_STEP);
  }
  return fill;
}

function settleFillAndOn(startFill: string, theme: ThemeName): { fill: string; on: string } {
  let fill = startFill;
  let on = bestOn(fill);
  for (let i = 0; i < MAX_ADJUSTMENTS && contrast(on, blend(on, fill, PRESSED_OVERLAY)) < MIN_TEXT_CONTRAST; i++) {
    const towardsLight = theme === "dark" || on !== ON_CANDIDATES[0];
    fill = shiftLightness(fill, towardsLight ? LIGHTNESS_STEP : -LIGHTNESS_STEP);
    on = bestOn(fill);
  }
  return { fill, on };
}

function deriveSoft(brand: string, surfaces: ThemeSurfaces): string {
  let alpha = surfaces.softAlpha;
  let soft = blend(brand, surfaces.page, alpha);
  while (contrast(surfaces.muted, soft) < MIN_TEXT_CONTRAST && alpha > SOFT_ALPHA_FLOOR) {
    alpha -= SOFT_ALPHA_STEP;
    soft = blend(brand, surfaces.page, alpha);
  }
  return soft;
}

function deriveInk(brand: string, soft: string, theme: ThemeName, surfaces: ThemeSurfaces): string {
  const backgrounds = [surfaces.page, surfaces.raised, surfaces.sunken, soft];
  const step = theme === "light" ? -INK_LIGHTNESS_STEP : INK_LIGHTNESS_STEP;
  let ink = brand;
  for (let i = 0; i < MAX_ADJUSTMENTS * 2 && Math.min(...backgrounds.map((b) => contrast(ink, b))) < MIN_TEXT_CONTRAST; i++) {
    ink = shiftLightness(ink, step);
  }
  return ink;
}

function deriveForTheme(requested: string | null, theme: ThemeName): AccentSet {
  const surfaces = THEMES[theme];
  const brand = requested ?? surfaces.inkDefault;
  const startFill = theme === "dark" ? liftFillAboveDarkPage(brand, surfaces.page) : brand;
  const { fill, on } = settleFillAndOn(startFill, theme);
  const soft = deriveSoft(brand, surfaces);
  return {
    fill,
    on,
    hover: blend(on, fill, HOVER_OVERLAY),
    pressed: blend(on, fill, PRESSED_OVERLAY),
    soft,
    ink: deriveInk(brand, soft, theme, surfaces),
  };
}

/** `#RRGGBB` en mayúsculas, o `null` si la entrada no es un hex válido (frontera de confianza). */
export function parseBrandHex(value: string | null | undefined): string | null {
  return value && BRAND_PATTERN.test(value) ? value.toUpperCase() : null;
}

/**
 * Deriva el acento de un cliente para ambos temas a partir del color confirmado de su kit de marca.
 * La entrada es una frontera de confianza: cualquier cosa que no sea `#RRGGBB` se trata como «sin marca».
 */
export function deriveClientAccent(brandHex: string | null | undefined): ClientAccent {
  const brand = parseBrandHex(brandHex);
  return { light: deriveForTheme(brand, "light"), dark: deriveForTheme(brand, "dark") };
}
